import React, { useState } from "react";
import PropTypes from "prop-types";
import { useFormik } from "formik";
import { Form as BTForm, Row, ProgressBar, Col } from "react-bootstrap";
import { useDispatch } from "react-redux";

import { Button } from "components/Button";
import { Form } from "components/Form";
import { Loader } from "components/Loader";
import { toast } from "Layout/slice";
import { parseErrorMessage } from "utils";
import { QuestionContainer } from "./QuestionModal.styles";
import RecommendedModalHeader from "../ModalHeader";
import { saveQuestionAnswers } from "services/index";
import { useQuestionnaire } from "shared/hooks/useQuestionnaire";

const QuestionModal = ({
  show,
  onHide,
  onSuccess,
  reviewQuestionChoice: initialChoice,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const dispatch = useDispatch();

  const { isLoading, questions } = useQuestionnaire("recommendation");

  const initialValues = {
    answers:
      initialChoice?.answers?.reduce((acc, answer) => {
        if (answer?.question?.id && answer?.choice?.id) {
          acc[answer.question.id] = answer.choice.id;
        }
        return acc;
      }, {}) || {},
  };

  const handleSubmit = async (values) => {
    const formattedAnswers = Object.entries(values?.answers)?.map(
      ([questionId, choiceId]) => ({
        question: questionId,
        choice: choiceId,
      })
    );

    if (currentStep < questions.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      try {
        const payload = {
          answers: formattedAnswers,
        };

        const response = await saveQuestionAnswers(payload);
        onSuccess(response);
      } catch (error) {
        dispatch(
          toast({
            type: "error",
            body: parseErrorMessage(error),
          })
        );
      } finally {
        onHide();
      }
    }
  };

  const formik = useFormik({
    initialValues: initialValues,
    onSubmit: handleSubmit,
    enableReinitialize: true,
  });

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleChange = (e) => {
    formik.setFieldValue(
      `answers.${questions[currentStep].id}`,
      e.target.value
    );
  };

  const progressPercentage = ((currentStep + 1) / questions?.length) * 100;

  return (
    <QuestionContainer show={show} onHide={onHide}>
      <RecommendedModalHeader />

      <div className="body-content">
        {!isLoading && (
          <Row className="progress-row">
            <Col md={10} xs={9}>
              <ProgressBar now={progressPercentage} />
            </Col>
            <Col md={2} xs={3} className="steps">
              Steps {currentStep + 1}/{questions?.length}
            </Col>
          </Row>
        )}

        <Form onSubmit={formik.handleSubmit}>
          {isLoading ? (
            <div className="loader">
              <Loader />
            </div>
          ) : (
            questions?.length > 0 && (
              <>
                <div className="question">{questions[currentStep]?.text}</div>
                {questions[currentStep]?.choices.map((option, index) => (
                  <div key={index} className="options">
                    <BTForm.Check
                      type="radio"
                      name={`answer${currentStep}`}
                      id={`answer${currentStep}_${index}`}
                      value={option?.id}
                      checked={
                        formik.values.answers[questions[currentStep].id] ===
                        String(option?.id)
                      }
                      onChange={handleChange}
                      label={option?.text}
                    />
                  </div>
                ))}
              </>
            )
          )}

          <Row className="button-wrapper">
            {currentStep > 0 && (
              <Col>
                <Button
                  variant="secondary"
                  onClick={handleBack}
                  className="w-100 "
                  disabled={isLoading}
                >
                  Back
                </Button>
              </Col>
            )}
            <Col>
              <Button
                type="submit"
                className="w-100"
                disabled={
                  !formik.values.answers[questions[currentStep]?.id] ||
                  isLoading
                }
              >
                {currentStep === questions?.length - 1 ? "Submit" : "Next"}
              </Button>
            </Col>
          </Row>
        </Form>
      </div>
    </QuestionContainer>
  );
};

QuestionModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  reviewQuestionChoice: PropTypes.object,
};

export { QuestionModal };
